/**
 * Hook to fetch declared outputs for flows referenced by `call_flow` nodes.
 *
 * @module FlowDesigner/hooks/useCallFlowNodeOutputs
 *
 * Mirrors `useSeedNodeOutputs`, but simpler: a `call_flow` node references an
 * arbitrary flow (not necessarily a seed script) and has no transitive-seed
 * concept, so this only fetches the direct set of referenced flows' outputs.
 * Shares the `['workflow', id, 'seed-outputs']` query key with
 * `useSeedNodeOutputs` AND `useSeedWorkflowOutputs` — all three now store the
 * same raw `SeedOutputsResponse` (`{ ok, outputs, workflowName? }`) under that
 * key, so whichever one services a refetch leaves a shape every observer can
 * read. Results are still indexed positionally (`flowIds[i]`, not
 * `q.data.id`) since the raw response has no id. See the comment on the map
 * construction below.
 *
 * `CallFlowNodeConfig` hits the SAME endpoint but is deliberately keyed
 * `['workflow', id, 'call-flow-inputs']`, because it stores a different
 * projection (`data.inputs` as `FlowValueInput[]`).
 */
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { WorkflowNode } from '../../../../types/workflow';
import { API_URL } from '../../../../config';
import type {
  SeedDataOutput,
  SeedOutputsResponse,
} from '../../../../hooks/useSeedWorkflowOutputs';

/**
 * For each `call_flow` node, fetches the referenced flow's declared outputs.
 * Returns a Map of flowId → SeedDataOutput[].
 */
export function useCallFlowNodeOutputs(nodes: WorkflowNode[]) {
  const flowIds = useMemo(() => {
    const ids = new Set<string>();
    for (const node of nodes) {
      if (node.type === 'call_flow') {
        const data = node.data as Record<string, unknown> | undefined;
        const flowId = data?.flowId as string | undefined;
        if (flowId) ids.add(flowId);
      }
    }
    return Array.from(ids);
  }, [nodes]);

  const outputQueries = useQueries({
    queries: flowIds.map((id) => ({
      queryKey: ['workflow', id, 'seed-outputs'],
      queryFn: async (): Promise<SeedOutputsResponse> => {
        const res = await fetch(`${API_URL}/workflows/${id}/seed-outputs`, {
          credentials: 'include',
        });
        if (!res.ok) return { ok: false, outputs: [] };
        return res.json();
      },
      staleTime: 60_000,
    })),
  });

  const callFlowOutputsByFlowId = useMemo(() => {
    const map = new Map<string, SeedDataOutput[]>();
    // Key off the request order, NOT a field on the response. `useQueries`
    // returns results positionally, so `flowIds[i]` is always right — and the
    // raw `SeedOutputsResponse` stored under `['workflow', id, 'seed-outputs']`
    // has no id to read anyway (see module docblock for why all observers of
    // this key now share that shape).
    outputQueries.forEach((q, i) => {
      if (q.data) {
        map.set(flowIds[i], q.data.outputs);
      }
    });
    return map;
  }, [outputQueries, flowIds]);

  const isLoading = outputQueries.some((q) => q.isLoading);

  return { callFlowOutputsByFlowId, isLoading };
}
